import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Upload, Trash2, Image } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const AppSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('*');

      if (data) {
        const namesSetting = data.find(s => s.setting_key === 'company_name');
        const logoSetting = data.find(s => s.setting_key === 'company_logo');

        if (namesSetting) setCompanyName(namesSetting.setting_value || '');
        if (logoSetting?.setting_value) {
          setLogoPath(logoSetting.setting_value);
          const { data: urlData } = supabase.storage
            .from('company-assets')
            .getPublicUrl(logoSetting.setting_value);
          setLogoUrl(urlData.publicUrl);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please upload an image file', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      // Delete old logo if exists
      if (logoPath) {
        await supabase.storage.from('company-assets').remove([logoPath]);
      }

      // Upload new logo
      const fileName = `logo-${Date.now()}.${file.name.split('.').pop()}`;
      const { error: uploadError } = await supabase.storage
        .from('company-assets')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Save to settings
      await upsertSetting('company_logo', fileName);
      
      setLogoPath(fileName);
      const { data: urlData } = supabase.storage
        .from('company-assets')
        .getPublicUrl(fileName);
      setLogoUrl(urlData.publicUrl);

      toast({ title: 'Logo uploaded successfully' });
    } catch (error: any) {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!logoPath) return;

    try {
      await supabase.storage.from('company-assets').remove([logoPath]);
      await supabase.from('app_settings').delete().eq('setting_key', 'company_logo');
      setLogoPath(null);
      setLogoUrl(null);
      toast({ title: 'Logo removed' });
    } catch (error: any) {
      toast({ title: 'Error removing logo', description: error.message, variant: 'destructive' });
    }
  };

  const upsertSetting = async (key: string, value: string) => {
    const { data: existing } = await supabase
      .from('app_settings')
      .select('id')
      .eq('setting_key', key)
      .maybeSingle();

    if (existing) {
      await supabase
        .from('app_settings')
        .update({ setting_value: value })
        .eq('setting_key', key);
    } else {
      await supabase
        .from('app_settings')
        .insert({ setting_key: key, setting_value: value, setting_type: 'text' });
    }
  };

  const handleSaveCompanyName = async () => {
    setSaving(true);
    try {
      await upsertSetting('company_name', companyName);
      toast({ title: 'Settings saved successfully' });
    } catch (error: any) {
      toast({ title: 'Error saving settings', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">App Settings</h1>
        <p className="text-muted-foreground">Configure company branding and application settings</p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Company Branding</CardTitle>
            <CardDescription>
              Configure your company name and logo for printed candidate profiles
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <div className="flex gap-2">
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Enter company name"
                />
                <Button onClick={handleSaveCompanyName} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Company Logo</Label>
              <p className="text-sm text-muted-foreground">
                This logo will appear on printed candidate profiles
              </p>

              {logoUrl ? (
                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <img
                    src={logoUrl}
                    alt="Company Logo"
                    className="h-16 max-w-[200px] object-contain"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                      Replace
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveLogo}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <Image className="h-10 w-10 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Click to upload company logo</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG, or SVG recommended</p>
                    </>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppSettings;
